import {
  RequestStatusSchema,
  BlockStatusSchema,
} from "@src/sudozi-schemas/request-status-schema";
import { z } from "zod";

export const IntakeWorkflowRuleTypeSchema = z.enum([
  "APPROVAL",
  "TASK",
  "SEND_VENDOR_FORM",
  "SEND_DOCUMENT",
  "START_IRONCLAD_WORKFLOW",
  "NETSUITE_PURCHASE_ORDER",
  "ADDITIONAL_STEP",
  "CREATE_JIRA_ISSUE",
  "MIDDESK_BUSINESS_VERIFICATION",
  "WEBHOOK",
]);

export type IntakeWorkflowRuleType = z.infer<
  typeof IntakeWorkflowRuleTypeSchema
>;

const LevelGroupStatusSchema = z.object({
  id: z.string(),
  parentLevelGroupStatusId: z.string().nullable(),
  ruleType: IntakeWorkflowRuleTypeSchema,
  level: z.number(),
  sublevels: z.array(z.number()),
  status: RequestStatusSchema,
  approverNote: z.string(),
  label: z.string(),
  bypassedAt: z.string().datetime().nullable(),
  bypassedBy: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .nullable(),
  remindedBy: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .nullable(),
  createdBy: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .nullable(),
  assigneeUser: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .nullable(),
  createdAt: z.string().datetime(),
  remindedAt: z.string().datetime().nullable(),
  remindedWithNote: z.string().nullable(),
  approvedAt: z.string().datetime().nullable(),
  approvedBy: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .nullable(),
  delegateApproverUser: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .nullable(),
  isManagerApprover: z.boolean(),
  isRequestor: z.boolean(),
  departments: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .array(),
  businessUnits: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .array(),
  groups: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .array(),
  task: z
    .object({
      id: z.string(),
      description: z.string(),
    })
    .nullable(),
  sendVendorFormTask: z
    .object({
      id: z.string(),
      toVendorId: z.string().nullable(),
      pocEmail: z.string().nullable(),
      emailCopy: z.string().nullable(),
      vendorIntakeCategoryFormsToSend: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          status: RequestStatusSchema,
        }),
      ),
      vendorFormSubmissions: z.array(
        z.object({
          id: z.string(),
          isDraft: z.boolean(),
          status: RequestStatusSchema,
          intakeCategoryId: z.string(),
          answersLastModifiedAt: z.string().datetime().nullable(),
        }),
      ),
    })
    .nullable(),
  sendDocumentTask: z
    .object({
      id: z.string(),
      pocEmail: z.string().nullable(),
      emailCopy: z.string().nullable(),
      documents: z.array(z.object({ id: z.string(), displayName: z.string() })),
    })
    .nullable(),
  additionalStepTask: z
    .object({
      id: z.string(),
      steps: z.array(
        z.object({
          id: z.string(),
          label: z.string().nullable(),
          ordinal: z.number(),
        }),
      ),
    })
    .nullable(),
  startIroncladWorkflow: z
    .object({
      id: z.string(),
      ironcladWorkflowTemplateId: z.string(),
      ironcladWorkflowId: z.string().nullable(),
      ironcladReadableWorkflowId: z.string().nullable(),
      errorMessage: z.string().nullable(),
      signedDocument: z
        .object({
          id: z.string(),
          displayName: z.string(),
        })
        .nullable(),
      startedAt: z.string().nullable(),
    })
    .nullable(),
  createJiraIssueTask: z
    .object({
      id: z.string(),
      jiraIssueId: z.string().nullable(),
      jiraIssueCreatedAt: z.string().nullable(),
      jiraIssueKey: z.string().nullable(),
    })
    .nullable(),
  middeskBusinessVerificationTask: z
    .object({
      id: z.string(),
      businessName: z.string().nullable(),
      businessAddress1: z.string().nullable(),
      businessAddress2: z.string().nullable(),
      businessCity: z.string().nullable(),
      businessState: z.string().nullable(),
      businessPostalCode: z.string().nullable(),
      businessCountry: z.string().nullable(),
      businessTaxId: z.string().nullable(),
      vendor: z.object({ id: z.string(), name: z.string() }).nullable(),
      syncedAt: z.string().nullable(),
      errorMessage: z.string().nullable(),
    })
    .nullable(),
  netsuitePurchaseOrder: z
    .object({
      id: z.string(),
      erpId: z.string().nullable(),
      url: z.string().nullable(),
      syncedOn: z.string().nullable(),
      error: z.string().nullable(),
      transactionDate: z.string().nullable(),
      memo: z.string(),
      erpVendorId: z.string().nullable(),
      erpVendor: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      erpEntityId: z.string().nullable(),
      erpEntity: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      erpLocationId: z.string().nullable(),
      erpLocation: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      erpDepartmentMappingId: z.string().nullable(),
      erpDepartment: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      erpClassificationId: z.string().nullable(),
      erpClassification: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      lines: z.array(
        z.object({
          id: z.string(),
          description: z.string(),
          amount: z.number(),
          erpAccountId: z.string(),
          erpAccount: z.object({
            id: z.string(),
            name: z.string(),
          }),
          erpDepartmentMappingId: z.string().nullable(),
          erpDepartment: z
            .object({
              id: z.string(),
              name: z.string(),
            })
            .nullable(),
          erpClassificationId: z.string().nullable(),
          erpClassification: z
            .object({
              id: z.string(),
              name: z.string(),
            })
            .nullable(),
          erpLocationId: z.string().nullable(),
          erpLocation: z
            .object({
              id: z.string(),
              name: z.string(),
            })
            .nullable(),
        }),
      ),
    })
    .nullable(),
  webhook: z
    .object({
      id: z.string(),
      url: z.string(),
      requestBody: z.string().nullable(),
      responseBody: z.string().nullable(),
      responseStatusCode: z.number().nullable(),
      sentAt: z.string().datetime().nullable(),
    })
    .nullable(),
  blockStatuses: z.array(
    z.object({
      id: z.string(),
      createdAt: z.string().datetime(),
      user: z
        .object({
          id: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string(),
        })
        .nullable(),
      blockStatus: BlockStatusSchema,
    }),
  ),
  approverMatrixApproverCell: z
    .object({
      id: z.string(),
      approverMatrixApproverColumn: z.object({
        id: z.string(),
        name: z.string(),
      }),
    })
    .nullable(),
});

export const ApproverStatusSchema = z.object({
  status: RequestStatusSchema,
  user: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
  }),
  delegates: z.array(
    z.object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    }),
  ),
});

const LevelGroupStatusWithApproverStatusSchema = z
  .intersection(
    LevelGroupStatusSchema,
    z.object({
      approverStatuses: ApproverStatusSchema.array(),
    }),
  )
  .array();

export type LevelGroupStatusWithApproverStatus = z.infer<
  typeof LevelGroupStatusWithApproverStatusSchema
>;

export const GetIntakeSubmissionApprovalStatusOutput = z.object({
  isApproved: z.boolean(),
  levelStatuses: LevelGroupStatusWithApproverStatusSchema,
});

export type GetIntakeSubmissionApprovalStatusOutput = z.infer<
  typeof GetIntakeSubmissionApprovalStatusOutput
>;
